import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, CheckCircle2, Scale, ShieldCheck, Lock, Server, Cpu,
  Database, WifiOff, FileLock2, Gavel, Calculator, Building2, Euro,
  Brain, Fingerprint, HardDrive,
} from "lucide-react";
import { Eyebrow, SectionHeading } from "@/components/Section";
import { SEO } from "@/components/SEO";

const SITE_URL = "https://it-servicepoint.eu";

const BENEFITS = [
  { icon: Lock, title: "Daten bleiben im Haus", desc: "Ihre Mandanten- und Klientendaten verlassen niemals Ihr Unternehmen. Keine Cloud, kein US-Server, kein Drittanbieter – die KI läuft auf Ihrer eigenen Hardware." },
  { icon: Scale, title: "DSGVO ohne Grauzone", desc: "Keine Auftragsverarbeitung ins Ausland, kein wackliges EU-US-Datenschutzabkommen. Verarbeitung ausschließlich lokal – die sauberste Rechtsgrundlage, die es gibt." },
  { icon: WifiOff, title: "Funktioniert offline", desc: "Die KI arbeitet auch ohne Internetverbindung. Kein Ausfall, wenn ein Cloud-Dienst gestört ist, und keine Abhängigkeit von der Verfügbarkeit externer Anbieter." },
  { icon: Euro, title: "Kalkulierbare Fixkosten", desc: "Einmalige Anschaffung statt Pro-Nutzer-Abos, die mit jedem Mitarbeiter teurer werden. Keine Token-Abrechnung, keine bösen Überraschungen auf der Cloud-Rechnung." },
  { icon: Fingerprint, title: "Volle Kontrolle & Protokoll", desc: "Sie bestimmen, wer worauf zugreift. Jede Abfrage bleibt nachvollziehbar in Ihrem Netz – ideal für Revisionssicherheit und interne Compliance." },
  { icon: Brain, title: "Trainiert auf Ihr Wissen", desc: "Die KI lernt aus Ihren eigenen Dokumenten, Vorlagen und Fällen – ohne dass dieses Wissen jemals an einen externen Anbieter abfließt." },
];

const AUDIENCES = [
  {
    icon: Gavel,
    title: "Anwaltskanzleien",
    tag: "§ 203 StGB · Berufsgeheimnis",
    desc: "Schriftsätze entwerfen, Urteile und Akten durchsuchen, Verträge prüfen, Rechtsprechung zusammenfassen – ohne dass ein Wort an OpenAI, Microsoft oder Google gelangt. Als Berufsgeheimnisträger dürfen Sie Mandantendaten schlicht nicht in fremde Clouds geben. Eine lokale KI löst genau dieses Dilemma.",
    points: ["Mandantengeheimnis technisch gewahrt", "Schriftsatz- & Vertragsentwürfe in Sekunden", "Volltextsuche über den gesamten Aktenbestand"],
  },
  {
    icon: Calculator,
    title: "Steuerberater & Steuerbüros",
    tag: "§ 203 StGB · Mandantendaten",
    desc: "Jahresabschlüsse kommentieren, Belege vorsortieren, Mandantenanfragen beantworten, Steuerrecht recherchieren. Bilanz- und Lohndaten sind hochsensibel – mit einer lokalen KI nutzen Sie moderne Assistenz, ohne die Verschwiegenheitspflicht zu verletzen.",
    points: ["DATEV- & Belegdaten bleiben lokal", "Schnellere Mandantenkommunikation", "Recherche im Steuerrecht auf Knopfdruck"],
  },
  {
    icon: Building2,
    title: "Weitere Branchen",
    tag: "Ärzte · Ingenieure · Industrie",
    desc: "Arztpraxen mit Patientendaten, Ingenieurbüros mit Konstruktionsplänen, Personalabteilungen mit Bewerberdaten, Forschung mit Betriebsgeheimnissen: Überall dort, wo Vertraulichkeit Pflicht ist und Cloud-KI ausscheidet, ist die lokale KI die Antwort.",
    points: ["Patienten- & Personaldaten geschützt", "Betriebsgeheimnisse bleiben Betriebsgeheimnis", "Individuell auf Ihre Prozesse zugeschnitten"],
  },
];

const RISKS = [
  { title: "Berufsgeheimnis (§ 203 StGB)", desc: "Anwälte, Steuerberater und Ärzte machen sich strafbar, wenn sie fremde Geheimnisse unbefugt offenbaren. Das Hochladen von Mandantendaten in eine öffentliche KI kann genau das sein." },
  { title: "DSGVO & Drittlandtransfer", desc: "Cloud-KIs verarbeiten Daten oft auf US-Servern. Ein rechtssicherer Transfer ist aufwendig und angreifbar. Lokal verarbeitete Daten kennen dieses Problem nicht." },
  { title: "Trainings-Nutzung Ihrer Daten", desc: "Viele Anbieter behalten sich vor, Eingaben zur Verbesserung ihrer Modelle zu nutzen. Ihre vertraulichen Dokumente würden Teil eines fremden Trainingsdatensatzes." },
  { title: "Abhängigkeit & Preissprünge", desc: "Wer seine Prozesse auf ein Cloud-Abo stützt, ist Preiserhöhungen, Funktionsänderungen und Ausfällen des Anbieters ausgeliefert. Lokal gehört die Lösung Ihnen." },
];

const STACK = [
  { icon: Server, title: "Hardware-Appliance", desc: "Kompakte KI-Server mit leistungsfähiger GPU – dimensioniert auf Ihre Kanzlei- oder Bürogröße. Vom stillen Desktop-Gerät bis zum Rack-Server." },
  { icon: Cpu, title: "Open-Source-Modelle", desc: "Leistungsstarke, offene Sprachmodelle (z. B. Llama, Mistral) laufen vollständig auf Ihrer Hardware – keine Lizenzbindung an einen Cloud-Konzern." },
  { icon: Database, title: "Ihr eigenes Wissen (RAG)", desc: "Wir binden Ihre Dokumente, Vorlagen und Akten sicher an, sodass die KI aus Ihrem Fachwissen antwortet – lokal indexiert, nichts verlässt das Haus." },
  { icon: FileLock2, title: "Sichere Integration", desc: "Die KI kommt ins abgeschottete Segment Ihres Netzwerks, mit Benutzerrechten, Protokollierung und Backup – als sauberer Managed Service betrieben." },
];

const PROCESS = [
  { step: "01", title: "Analyse & Beratung", desc: "Wir klären Anwendungsfälle, Datenschutzanforderungen und die passende Modell- und Hardware-Größe für Ihr Haus." },
  { step: "02", title: "Aufbau der Appliance", desc: "Wir liefern und installieren den KI-Server, richten das Modell ein und binden Ihr Wissen sicher an." },
  { step: "03", title: "Integration & Schulung", desc: "Anbindung an Ihre Arbeitsumgebung, Benutzerrechte, kurze Einweisung Ihres Teams – startklar in wenigen Tagen." },
  { step: "04", title: "Betrieb & Wartung", desc: "Auf Wunsch als Managed Service: Updates, Backup, Monitoring und ein fester Ansprechpartner. Sie nutzen, wir kümmern uns." },
];

const FAQ = [
  { q: "Ist eine lokale KI wirklich DSGVO-konform?", a: "Ja – und zwar auf die sauberste denkbare Weise. Weil die Daten Ihr Unternehmen physisch nie verlassen, gibt es keinen Drittlandtransfer, keine Auftragsverarbeitung durch einen externen Anbieter und keine Nutzung Ihrer Eingaben zu Trainingszwecken. Die Verarbeitung findet ausschließlich auf Ihrer eigenen Hardware in Deutschland statt. Damit entfallen die größten Risiken, die Cloud-KI mit sich bringt." },
  { q: "Ist das nicht viel zu teuer für eine kleine Kanzlei?", a: "Nein. Statt monatlicher Pro-Nutzer-Abos, die mit jedem Mitarbeiter teurer werden, investieren Sie einmalig in eine Appliance, die zu Ihrer Größe passt. Für kleine Kanzleien und Büros genügt oft ein kompaktes, stromsparendes Gerät. Wir dimensionieren die Lösung so, dass sich die Anschaffung gegenüber laufenden Cloud-Kosten meist innerhalb überschaubarer Zeit rechnet." },
  { q: "Wie leistungsfähig ist eine lokale KI im Vergleich zu ChatGPT?", a: "Moderne offene Modelle wie Llama oder Mistral erreichen für die typischen Büroaufgaben – Texte entwerfen, zusammenfassen, recherchieren, Dokumente durchsuchen – ein sehr hohes Niveau. Für die meisten Kanzlei- und Bürotätigkeiten ist der Unterschied im Alltag kaum spürbar, der Datenschutzgewinn dafür enorm. Wir wählen das Modell passend zu Ihren Aufgaben aus." },
  { q: "Kann die KI auf unsere eigenen Akten und Vorlagen zugreifen?", a: "Ja. Über ein Verfahren namens RAG (Retrieval Augmented Generation) binden wir Ihre Dokumente, Vorlagen und Wissensdatenbanken lokal an. Die KI antwortet dann auf Basis Ihres eigenen Fachwissens – und all das bleibt indexiert auf Ihrer Hardware, ohne je nach außen zu gelangen." },
  { q: "Wer kümmert sich um Updates, Backup und Betrieb?", a: "Auf Wunsch übernehmen wir das komplett als Managed Service: Modell- und Systemupdates, Backup, Monitoring und Support mit festem Ansprechpartner. Sie konzentrieren sich auf Ihre Mandanten, wir halten die KI sicher und aktuell." },
  { q: "Wie lange dauert die Einführung?", a: "Nach dem Beratungsgespräch und der Auswahl der passenden Hardware ist eine Standard-Appliance in der Regel innerhalb weniger Tage einsatzbereit – inklusive Einrichtung, Anbindung Ihres Wissens und einer kurzen Einweisung Ihres Teams." },
];

export default function LokaleKI() {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "@id": `${SITE_URL}/lokale-ki#service`,
      name: "Lokale KI im eigenen Unternehmen betreiben",
      alternateName: ["Eigene KI", "On-Premise KI", "DSGVO-konforme KI", "Private KI", "KI für Anwaltskanzleien", "KI für Steuerberater"],
      provider: { "@id": `${SITE_URL}/#organization` },
      serviceType: "On-premise AI / local AI setup and hosting",
      url: `${SITE_URL}/lokale-ki`,
      image: `${SITE_URL}/wp-images/lokale-ki-hero.jpg`,
      description:
        "Beratung, Aufbau und Betrieb einer lokalen, DSGVO-konformen KI im eigenen Unternehmen – speziell für Anwaltskanzleien, Steuerberater und Unternehmen mit vertraulichen Daten. Die KI läuft auf eigener Hardware, Daten bleiben im Haus, kein Cloud-Zwang.",
      areaServed: [
        { "@type": "AdministrativeArea", name: "Hessen" },
        { "@type": "City", name: "Fulda" },
        { "@type": "City", name: "Bad Hersfeld" },
        { "@type": "City", name: "Kassel" },
        { "@type": "City", name: "Erfurt" },
        { "@type": "City", name: "Würzburg" },
        { "@type": "Country", name: "Deutschland" },
      ],
      audience: [
        { "@type": "Audience", audienceType: "Anwaltskanzleien" },
        { "@type": "Audience", audienceType: "Steuerberater und Steuerbüros" },
        { "@type": "Audience", audienceType: "Arztpraxen" },
        { "@type": "Audience", audienceType: "Mittelständische Unternehmen" },
      ],
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Lokale KI – Leistungen",
        itemListElement: [
          "KI-Beratung & Anwendungsfall-Analyse",
          "Aufbau der KI-Appliance (Hardware & Modell)",
          "Anbindung des eigenen Wissens (RAG)",
          "Betrieb & Wartung als Managed Service",
        ].map((n, i) => ({
          "@type": "Offer",
          position: i + 1,
          itemOffered: { "@type": "Service", name: n, provider: { "@id": `${SITE_URL}/#organization` } },
        })),
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Start", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "Lokale KI", item: `${SITE_URL}/lokale-ki` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  return (
    <div data-testid="local-ai-page" className="relative">
      <SEO
        title="Lokale KI im eigenen Unternehmen · DSGVO-konforme KI für Kanzleien & Steuerbüros"
        description="Eigene KI lokal betreiben statt Daten in die Cloud geben: DSGVO-konforme On-Premise-KI für Anwaltskanzleien, Steuerberater und Unternehmen mit vertraulichen Daten. Beratung, Aufbau und Betrieb aus Hessen (Fulda, Bad Hersfeld, Kassel) – Ihre Daten bleiben im Haus."
        keywords="lokale KI, eigene KI, KI im Unternehmen, On-Premise KI, DSGVO KI, DSGVO-konforme KI, KI für Anwaltskanzleien, KI für Steuerberater, KI für Steuerbüro, KI für Arztpraxis, private KI, KI ohne Cloud, KI Datenschutz, KI Berufsgeheimnis, eigene KI betreiben, KI Server Unternehmen, KI Fulda, KI Hessen, KI Kanzlei, KI Mittelstand"
        image={`${SITE_URL}/wp-images/lokale-ki-hero.jpg`}
        jsonLd={jsonLd}
        type="service"
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-ink text-white border-b border-line">
        <div className="absolute inset-0">
          <img
            src="/wp-images/lokale-ki-hero.jpg"
            alt="Lokaler KI-Server im eigenen Unternehmen – DSGVO-konforme KI ohne Cloud"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-ink/95 via-ink/80 to-ink/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 py-24 sm:py-36">
          <div className="max-w-3xl">
            <Eyebrow className="text-accent">Künstliche Intelligenz · 100 % im eigenen Haus</Eyebrow>
            <h1 className="mt-6 font-display font-black text-4xl sm:text-6xl lg:text-7xl tracking-tightest leading-[0.95]">
              Ihre eigene KI.<br />
              <span className="text-accent">Ihre Daten bleiben da.</span>
            </h1>
            <p className="mt-8 max-w-2xl text-lg sm:text-xl text-white/85 leading-relaxed">
              Nutzen Sie die Kraft moderner KI, <strong className="text-white">ohne vertrauliche Daten in fremde Clouds zu geben</strong>. Wir bauen und betreiben eine lokale KI direkt in Ihrem Unternehmen – DSGVO-konform, offlinefähig und trainiert auf Ihr eigenes Wissen. Gemacht für Anwaltskanzleien, Steuerbüros und alle, die Vertraulichkeit ernst nehmen.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/kontakt"
                data-testid="localai-hero-cta"
                className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-accent text-ink font-medium hover:bg-accent/90 transition-colors"
              >
                Kostenfreie KI-Beratung anfragen
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href="tel:+4966299153890"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/40 text-white hover:bg-white/10 transition-colors font-medium"
              >
                +49 (0) 6629 9153890
              </a>
            </div>
            <div className="mt-12 flex flex-wrap gap-3">
              {["Keine Cloud", "Daten bleiben im Haus", "DSGVO-konform", "Offlinefähig", "Made in Germany"].map((c) => (
                <span key={c} className="border border-white/20 bg-white/5 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/85">
                  {c}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* THE CLOUD PROBLEM */}
      <section className="bg-white border-b border-line">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-24 sm:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6">
              <Eyebrow>Das Problem mit Cloud-KI</Eyebrow>
              <h2 className="mt-4 font-display font-black text-4xl sm:text-5xl tracking-tightest leading-[1.02] text-ink">
                KI ist genial.<br />
                <span className="text-primary">Ihre Daten dorthin zu geben, ist riskant.</span>
              </h2>
              <p className="mt-6 text-muted text-lg leading-relaxed">
                Sobald ein Schriftsatz, ein Jahresabschluss oder eine Patientenakte in ChatGPT & Co. eingegeben wird, verlässt er Ihr Unternehmen – oft auf Server außerhalb der EU. Für Berufsgeheimnisträger ist das nicht nur ein Datenschutz-, sondern ein <strong className="text-ink">strafrechtliches</strong> Problem.
              </p>
              <p className="mt-4 text-muted text-lg leading-relaxed">
                Die Lösung ist nicht, auf KI zu verzichten. Die Lösung ist, die KI zu <strong className="text-ink">Ihnen</strong> zu holen.
              </p>
            </div>
            <div className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {RISKS.map((r, i) => (
                <div key={r.title} className="border border-line bg-soft p-6" data-testid={`localai-risk-${i}`}>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent">Risiko {String(i + 1).padStart(2, "0")}</div>
                  <h3 className="mt-3 font-display font-bold text-base text-ink leading-tight">{r.title}</h3>
                  <p className="mt-2 text-muted text-sm leading-relaxed">{r.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="bg-soft border-b border-line">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-24 sm:py-32">
          <SectionHeading
            eyebrow="Vorteile einer eigenen KI"
            title="Warum die KI ins eigene Haus gehört."
            sub="Eine lokal betriebene KI verbindet die Leistungsfähigkeit moderner Sprachmodelle mit einem Datenschutzniveau, das keine Cloud bieten kann."
          />
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {BENEFITS.map((b, i) => (
              <div key={b.title} className="border border-line bg-white p-8 lift" data-testid={`localai-benefit-${i}`}>
                <div className="w-11 h-11 bg-soft border border-line flex items-center justify-center text-primary mb-5">
                  <b.icon size={20} />
                </div>
                <h3 className="font-display font-bold text-lg text-ink leading-tight">{b.title}</h3>
                <p className="mt-3 text-muted text-sm leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AUDIENCES */}
      <section className="bg-white border-b border-line">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-24 sm:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-16">
            <div className="lg:col-span-8">
              <Eyebrow>Für wen sich das besonders lohnt</Eyebrow>
              <h2 className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tightest leading-[1.02] text-ink">
                Gemacht für Berufe mit<br />
                <span className="text-primary">Schweigepflicht.</span>
              </h2>
            </div>
            <div className="lg:col-span-4">
              <p className="text-muted text-lg leading-relaxed">
                Überall dort, wo Daten vertraulich sind und das Berufsgeheimnis gilt, ist Cloud-KI ein Tabu – und eine lokale KI der einzig saubere Weg.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {AUDIENCES.map((a, i) => (
              <article key={a.title} className="border border-line bg-white p-8 flex flex-col lift" data-testid={`localai-audience-${i}`}>
                <div className="w-12 h-12 bg-primary/5 border border-line flex items-center justify-center text-primary mb-5">
                  <a.icon size={22} />
                </div>
                <span className="self-start bg-soft border border-line px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-accent">{a.tag}</span>
                <h3 className="mt-4 font-display font-bold text-2xl text-ink">{a.title}</h3>
                <p className="mt-4 text-muted text-sm leading-relaxed flex-1">{a.desc}</p>
                <ul className="mt-6 space-y-2 border-t border-line pt-5">
                  {a.points.map((p) => (
                    <li key={p} className="flex items-start gap-2 text-ink text-sm">
                      <CheckCircle2 size={16} className="text-primary shrink-0 mt-0.5" /> {p}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* DSGVO / DATENSCHUTZ HIGHLIGHT */}
      <section className="bg-ink text-white border-b border-line relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/wp-images/lokale-ki-datenschutz.jpg"
            alt="Datensouveränität – lokale KI ohne Datenabfluss in die Cloud"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/85 to-ink/60" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 py-20 sm:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-accent">
              <Scale size={14} /> DSGVO · § 203 StGB · Datensouveränität
            </div>
            <h2 className="mt-5 font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tightest leading-[1.05]">
              Datenschutz ist kein Hindernis.<br />Er ist Ihr Verkaufsargument.
            </h2>
            <p className="mt-5 text-white/80 text-lg leading-relaxed max-w-2xl">
              Wer heute belegen kann, dass Mandanten- und Kundendaten das eigene Haus nie verlassen, gewinnt Vertrauen – und hebt sich von Wettbewerbern ab, die ihre Daten in fremde Clouds schieben. Eine lokale KI macht diesen Vorsprung technisch nachweisbar.
            </p>
          </div>
          <div className="lg:col-span-5">
            <ul className="space-y-3">
              {[
                "Kein Drittlandtransfer (Art. 44 ff. DSGVO)",
                "Keine Auftragsverarbeitung durch Dritte",
                "Keine Nutzung Ihrer Daten zum KI-Training",
                "Berufsgeheimnis nach § 203 StGB gewahrt",
                "Verarbeitung ausschließlich in Deutschland",
                "Revisionssichere Protokollierung im eigenen Netz",
              ].map((p) => (
                <li key={p} className="flex items-start gap-2 text-white/90 text-sm">
                  <ShieldCheck size={16} className="text-accent shrink-0 mt-0.5" /> {p}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* WHAT WE BUILD / STACK */}
      <section className="bg-soft border-b border-line">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-24 sm:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-16">
            <div className="lg:col-span-5">
              <div className="relative overflow-hidden border border-line">
                <img
                  src="/wp-images/lokale-ki-kanzlei.jpg"
                  alt="Anwalt und Steuerberater nutzen eine lokale KI im Büro"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
            <div className="lg:col-span-7">
              <Eyebrow>Was wir für Sie aufbauen</Eyebrow>
              <h2 className="mt-4 font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tightest leading-[1.05] text-ink">
                Schlüsselfertige KI-Appliance – schlank oder skaliert.
              </h2>
              <p className="mt-5 text-muted text-lg leading-relaxed">
                Vom kompakten, stromsparenden Gerät für die kleine Kanzlei bis zum leistungsstarken Rack-Server für größere Häuser: Wir dimensionieren, installieren und betreiben Ihre KI passend zu Ihren Anwendungsfällen.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {STACK.map((s, i) => (
              <div key={s.title} className="border border-line bg-white p-7 lift" data-testid={`localai-stack-${i}`}>
                <div className="w-11 h-11 bg-soft border border-line flex items-center justify-center text-primary mb-5">
                  <s.icon size={20} />
                </div>
                <h3 className="font-display font-bold text-lg text-ink leading-tight">{s.title}</h3>
                <p className="mt-3 text-muted text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-white border-b border-line">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-24 sm:py-32">
          <SectionHeading
            eyebrow="So läuft die Einführung"
            title="In wenigen Tagen zur eigenen KI."
            sub="Ein klarer, planbarer Weg – von der ersten Beratung bis zum laufenden Betrieb als Managed Service."
          />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PROCESS.map((p, i) => (
              <div key={p.step} className="relative border border-line bg-white p-8 lift" data-testid={`localai-process-${i}`}>
                <div className="font-display font-black text-5xl tracking-tightest text-primary/15">{p.step}</div>
                <h3 className="mt-3 font-display font-bold text-lg text-ink">{p.title}</h3>
                <p className="mt-3 text-muted text-sm leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-soft border-b border-line">
        <div className="max-w-4xl mx-auto px-6 sm:px-12 py-24 sm:py-28">
          <Eyebrow>Häufige Fragen</Eyebrow>
          <h2 className="mt-4 font-display font-black text-3xl sm:text-5xl tracking-tightest text-ink">FAQ.</h2>
          <div className="mt-12 space-y-4">
            {FAQ.map((f, i) => (
              <details key={i} className="border border-line bg-white p-6 group" data-testid={`localai-faq-${i}`}>
                <summary className="font-display font-bold text-lg text-ink cursor-pointer flex items-center justify-between gap-4">
                  {f.q}
                  <span className="text-primary group-open:rotate-45 transition-transform text-xl">+</span>
                </summary>
                <p className="mt-4 text-muted leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 pointer-events-none" style={{
          backgroundImage: "radial-gradient(rgba(255,255,255,0.15) 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }} />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 py-20 sm:py-28 text-center">
          <Cpu size={40} className="text-accent mx-auto" />
          <h2 className="mt-6 font-display font-black text-3xl sm:text-5xl tracking-tightest leading-[1.05] max-w-3xl mx-auto">
            Bereit für KI, die Ihre Geheimnisse behält?
          </h2>
          <p className="mt-6 max-w-2xl mx-auto text-white/80 text-lg">
            30 Minuten Erstgespräch · kostenfrei · unverbindlich. Wir klären Ihre Anwendungsfälle, den Datenschutz und welche KI-Appliance zu Ihrem Haus passt – für Kanzleien, Steuerbüros und Unternehmen in Fulda, Bad Hersfeld, Kassel und ganz Deutschland.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/kontakt"
              data-testid="localai-cta-contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-accent text-ink font-medium hover:bg-accent/90 transition-colors"
            >
              KI-Beratung anfragen <ArrowRight size={18} />
            </Link>
            <a
              href="tel:+4966299153890"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/40 text-white hover:bg-white/10 transition-colors"
            >
              +49 (0) 6629 9153890
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
